// Génération des secrets d'une infra client.
//
// Le template livre des placeholders (`change-me`, `admin`) qui doivent être
// remplacés AVANT le premier push : `--provision` pousse le repo et crée les
// ressources Coolify, donc un placeholder oublié devient un mot de passe de prod.
// Tout part de `randomBytes` — jamais de `Math.random`.

import { randomBytes, randomInt } from "node:crypto"

/**
 * Plancher vérifié à l'exécution par la coque client
 * (`apps/webapp/src/server/session.ts`), qui refuse de démarrer en dessous.
 * On génère largement au-dessus ; la constante documente la contrainte.
 */
export const SESSION_SECRET_MIN_LENGTH = 32

/** Octets d'entropie du `SESSION_SECRET` (48 o → 64 caractères en base64). */
const SESSION_SECRET_BYTES = 48

/** Octets d'entropie des mots de passe (24 o → 32 caractères en base64url). */
const PASSWORD_BYTES = 24

export interface GeneratedSecrets {
	/** Scellage des cookies de session de la webapp. */
	sessionSecret: string
	/** Mot de passe applicatif MariaDB (`WORDPRESS_DB_PASSWORD`). */
	dbPassword: string
	/** Mot de passe root MariaDB (`MYSQL_ROOT_PASSWORD`). */
	dbRootPassword: string
	/** `WP_REDIS_PASSWORD`. */
	redisPassword: string
	/** Mot de passe du compte admin WordPress créé par le bootstrap. */
	wpAdminPassword: string
	/** `CACHE_PURGE_SECRET` — partagé webapp ↔ WordPress pour le webhook de purge. */
	purgeSecret: string
}

/**
 * Mot de passe base64url : ni `+`, ni `/`, ni `=`.
 *
 * Ces valeurs traversent des couches qui les recasseraient sinon — URL de
 * connexion MariaDB, `redis-server --requirepass`, interpolation Docker Compose
 * (où `$` déclenche une substitution). L'alphabet base64url est sûr partout.
 */
function randomPassword(bytes = PASSWORD_BYTES): string {
	return randomBytes(bytes).toString("base64url")
}

/**
 * `SESSION_SECRET` en base64 standard : il n'est lu que par `node:crypto` côté
 * webapp, donc l'alphabet complet est sans risque et rend l'entropie plus dense.
 */
function randomSessionSecret(): string {
	return randomBytes(SESSION_SECRET_BYTES).toString("base64")
}

/** Génère l'intégralité des secrets d'une infra. Chaque appel produit des valeurs neuves. */
export function generateSecrets(): GeneratedSecrets {
	return {
		sessionSecret: randomSessionSecret(),
		dbPassword: randomPassword(),
		dbRootPassword: randomPassword(),
		redisPassword: randomPassword(),
		wpAdminPassword: randomPassword(),
		purgeSecret: randomPassword(),
	}
}

/**
 * Suffixe aléatoire court, pour désambiguïser un nom de ressource Coolify quand
 * un projet du même nom existe déjà. `randomInt` évite le biais modulo.
 */
export function randomSuffix(length = 6): string {
	const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789"
	let out = ""
	for (let i = 0; i < length; i += 1) {
		out += alphabet[randomInt(alphabet.length)]
	}
	return out
}

/** Ensemble des valeurs à masquer dans les logs et le `--dry-run`. */
export function secretValues(secrets: GeneratedSecrets): string[] {
	return Object.values(secrets).filter((v) => v.length > 0)
}

/**
 * Remplace toute occurrence d'un secret par `***` dans un texte destiné au
 * terminal. Le `--dry-run` imprime des payloads complets : sans ce filtre, il
 * recracherait les mots de passe de prod dans l'historique du shell.
 *
 * Le tri par longueur décroissante évite qu'un secret court, contenu dans un
 * secret long, ne le masque partiellement et ne laisse fuiter le reste.
 */
export function redact(text: string, values: readonly string[]): string {
	let out = text
	for (const value of [...values].sort((a, b) => b.length - a.length)) {
		if (value.length < 8) continue
		out = out.split(value).join("***")
	}
	return out
}
